import React, { useEffect, useMemo, useState } from 'react';

// Details of every schedule clash, who most likely caused it, and a ready-to-send
// WhatsApp message to ask that person to fix it.
const fmtTime = (v) => {
  if (!v) return '';
  const [h, m] = String(v).split(':');
  const hour = Number(h);
  return `${hour % 12 === 0 ? 12 : hour % 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
};

const fmtLongDate = (ymd) =>
  new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' })
    .format(new Date(`${ymd}T00:00:00`));

const fmtStamp = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-LK', {
    timeZone: 'Asia/Colombo', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  }).format(d);
};

// The entry edited most recently is the one that most likely introduced the clash.
function analyse(conflict) {
  const [a, b] = conflict.entries;
  const cause = conflict.entries.find((e) => e.key === conflict.lastEditedKey) || a;
  const other = cause === a ? b : a;
  const edited = !!cause.wasEverEdited;
  return {
    a,
    b,
    cause,
    other,
    culprit: edited ? cause.updatedBy : cause.createdBy,
    action: edited ? 'updated' : 'scheduled',
    when: fmtStamp(edited ? cause.updatedAt : cause.createdAt),
    reason:
      conflict.type === 'both'
        ? `Room (${conflict.roomResource}) and lecturer (${conflict.lecturerResource}) are both double-booked`
        : conflict.type === 'room'
          ? `Room (${conflict.resource}) is double-booked`
          : `Lecturer (${conflict.resource}) is double-booked`,
  };
}

function buildMessage(conflict) {
  const x = analyse(conflict);
  const greet = x.culprit && x.culprit !== 'System' ? `Hi ${x.culprit},` : 'Hi,';
  return [
    '⚠️ *Timetable clash – please correct*',
    greet,
    '',
    `*${x.reason}*`,
    `📅 ${fmtLongDate(conflict.date)}`,
    `⏰ ${fmtTime(x.a.startTime)} – ${fmtTime(x.a.endTime)}`,
    '',
    `1) ${x.a.label}${x.a.lecturer ? ` (${x.a.lecturer})` : ''}${x.a.room ? ` – ${x.a.room}` : ''}`,
    `2) ${x.b.label}${x.b.lecturer ? ` (${x.b.lecturer})` : ''}${x.b.room ? ` – ${x.b.room}` : ''}`,
    '',
    `The entry "${x.cause.label}" was ${x.action} by ${x.culprit}${x.when ? ` on ${x.when}` : ''}.`,
    'Please change the room, lecturer or time so they do not overlap, and reply here once it is fixed. Thank you 🙏',
  ].join('\n');
}

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (_) {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch (__) {
      return false;
    }
  }
}

function CopyButtons({ text, id, copiedId, onCopied, label = 'Copy WhatsApp message' }) {
  return (
    <div className="flex flex-wrap gap-2">
      <button
        type="button"
        onClick={async () => { if (await copyText(text)) onCopied(id); }}
        className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow transition hover:bg-emerald-700"
      >
        {copiedId === id ? '✓ Copied' : `📋 ${label}`}
      </button>
      <a
        href={`https://wa.me/?text=${encodeURIComponent(text)}`}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-50"
      >
        Open WhatsApp
      </a>
    </div>
  );
}

export default function ScheduleConflictModal({ conflicts, onClose }) {
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (copiedId == null) return undefined;
    const t = setTimeout(() => setCopiedId(null), 2000);
    return () => clearTimeout(t);
  }, [copiedId]);

  const items = useMemo(() => conflicts.map((c, i) => ({ c, i, x: analyse(c), msg: buildMessage(c) })), [conflicts]);

  // One combined message per person, so they get a single WhatsApp listing every clash to fix.
  const byPerson = useMemo(() => {
    const m = new Map();
    items.forEach((it) => {
      const name = it.x.culprit || 'Unknown';
      if (!m.has(name)) m.set(name, []);
      m.get(name).push(it);
    });
    return Array.from(m.entries()).map(([name, list]) => ({
      name,
      count: list.length,
      text: list.length === 1
        ? list[0].msg
        : [
          `⚠️ *${list.length} timetable clashes – please correct*`,
          name !== 'System' ? `Hi ${name},` : 'Hi,',
          '',
          ...list.flatMap((it, n) => [
            `*${n + 1}. ${it.x.reason}*`,
            `📅 ${fmtLongDate(it.c.date)}  ⏰ ${fmtTime(it.x.a.startTime)} – ${fmtTime(it.x.a.endTime)}`,
            `   • ${it.x.a.label}`,
            `   • ${it.x.b.label}`,
            `   Your change: "${it.x.cause.label}" (${it.x.action}${it.x.when ? ` ${it.x.when}` : ''})`,
            '',
          ]),
          'Please change the room, lecturer or time so they do not overlap, and reply here once fixed. Thank you 🙏',
        ].join('\n'),
    }));
  }, [items]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 sm:p-8" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="conflict-modal-title"
        className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 p-5">
          <div>
            <h2 id="conflict-modal-title" className="flex items-center gap-2 text-lg font-bold text-gray-900">
              <span className="text-rose-600">⚠️</span> Schedule Conflicts
            </h2>
            <p className="mt-0.5 text-sm text-gray-500">
              {conflicts.length} clash{conflicts.length === 1 ? '' : 'es'} · the highlighted person made the latest change
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="space-y-5 p-5">
          {byPerson.length > 0 && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
              <p className="mb-2 text-sm font-bold text-emerald-900">Message by person</p>
              <div className="space-y-2">
                {byPerson.map((p) => (
                  <div key={p.name} className="flex flex-col gap-2 rounded-lg bg-white p-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-sm text-gray-800">
                      <span className="font-semibold">{p.name}</span>
                      <span className="ml-2 rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-bold text-rose-700">{p.count} to fix</span>
                    </div>
                    <CopyButtons text={p.text} id={`p:${p.name}`} copiedId={copiedId} onCopied={setCopiedId} label="Copy message" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {items.map(({ c, i, x, msg }) => (
            <div key={i} className="overflow-hidden rounded-xl border border-gray-200">
              <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 bg-gray-50 px-4 py-2.5">
                <span className="rounded bg-gray-800 px-2 py-0.5 text-[11px] font-bold uppercase text-white">{fmtLongDate(c.date)}</span>
                <span className="text-xs font-semibold text-gray-600">{fmtTime(x.a.startTime)} – {fmtTime(x.a.endTime)}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${c.type === 'room' ? 'bg-sky-100 text-sky-700' : c.type === 'lecturer' ? 'bg-violet-100 text-violet-700' : 'bg-fuchsia-100 text-fuchsia-700'}`}>
                  {x.reason}
                </span>
              </div>

              <div className="grid gap-3 p-4 sm:grid-cols-2">
                {[x.a, x.b].map((e) => {
                  const isCause = e.key === c.lastEditedKey;
                  const edited = !!e.wasEverEdited;
                  return (
                    <div key={e.key} className={`rounded-lg border p-3 text-sm ${isCause ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-white'}`}>
                      {isCause && <span className="mb-1 inline-block rounded bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white">Latest change</span>}
                      <p className="font-semibold text-gray-900">{e.label}</p>
                      <p className="mt-1 text-xs text-gray-600">
                        {e.lecturer ? `👨‍🏫 ${e.lecturer}` : '👨‍🏫 No lecturer'} · {e.room ? `🏫 ${e.room}` : '🏫 No room'}
                      </p>
                      <p className="mt-1.5 text-xs text-gray-500">
                        {edited ? 'Updated' : 'Scheduled'} by{' '}
                        <span className={`font-semibold ${isCause ? 'text-amber-800' : 'text-gray-700'}`}>{edited ? e.updatedBy : e.createdBy}</span>
                        {fmtStamp(edited ? e.updatedAt : e.createdAt) ? ` · ${fmtStamp(edited ? e.updatedAt : e.createdAt)}` : ''}
                      </p>
                    </div>
                  );
                })}
              </div>

              <div className="border-t border-gray-200 bg-gray-50/60 p-4">
                <p className="mb-1.5 text-xs font-semibold text-gray-500">
                  Message to <span className="text-gray-800">{x.culprit}</span>
                </p>
                <pre className="mb-3 whitespace-pre-wrap rounded-lg border border-gray-200 bg-white p-3 font-sans text-xs leading-relaxed text-gray-700">{msg}</pre>
                <CopyButtons text={msg} id={`c:${i}`} copiedId={copiedId} onCopied={setCopiedId} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
